package com.iptvplayer.shared.data.repository

import com.iptvplayer.shared.data.remote.api.WatchHistoryApi
import com.iptvplayer.shared.data.remote.dto.UpdateHistoryRequestDto
import com.iptvplayer.shared.domain.model.ContentType
import com.iptvplayer.shared.domain.model.WatchHistory
import com.iptvplayer.shared.domain.repository.WatchHistoryRepository
import com.iptvplayer.shared.util.Result
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.map

class WatchHistoryRepositoryImpl(
    private val watchHistoryApi: WatchHistoryApi
) : WatchHistoryRepository {

    private val historyFlow = MutableStateFlow<List<WatchHistory>>(emptyList())

    override fun getHistory(limit: Int): Flow<List<WatchHistory>> =
        historyFlow.map { list ->
            list.sortedByDescending { it.lastWatchedAt }.take(limit)
        }

    override suspend fun updateProgress(
        contentId: String,
        contentType: ContentType,
        contentName: String,
        contentLogoUrl: String?,
        streamUrl: String,
        resumePositionMs: Long,
        durationMs: Long?,
        seriesSourceId: String?,
        seriesId: Int?
    ): Result<WatchHistory> {
        // 1) Optimistic: update the flow first so Continue Watching shows the
        //    row immediately, even before the backend round-trip completes.
        val now = nowMs()
        val optimistic = WatchHistory(
            id = "local-$contentId",
            userId = "",
            contentId = contentId,
            contentType = contentType,
            contentName = contentName,
            contentLogoUrl = contentLogoUrl,
            streamUrl = streamUrl,
            resumePositionMs = resumePositionMs,
            durationMs = durationMs,
            lastWatchedAt = now,
            seriesSourceId = seriesSourceId,
            seriesId = seriesId,
        )
        upsertLocal(optimistic)

        return try {
            val dto = watchHistoryApi.updateProgress(
                UpdateHistoryRequestDto(
                    contentId = contentId,
                    contentType = contentType.name,
                    contentName = contentName,
                    contentLogoUrl = contentLogoUrl,
                    streamUrl = streamUrl,
                    resumePositionMs = resumePositionMs,
                    durationMs = durationMs,
                    seriesSourceId = seriesSourceId,
                    seriesId = seriesId?.toLong()
                )
            )
            val history = WatchHistory(
                id = dto.id,
                userId = dto.userId,
                contentId = dto.contentId,
                contentType = parseContentType(dto.contentType),
                contentName = dto.contentName,
                contentLogoUrl = dto.contentLogoUrl,
                streamUrl = dto.streamUrl,
                resumePositionMs = dto.resumePositionMs,
                durationMs = dto.durationMs,
                lastWatchedAt = dto.lastWatchedAt,
                seriesSourceId = dto.seriesSourceId,
                seriesId = dto.seriesId?.toInt()
            )
            // Replace the optimistic entry with the server-authoritative one.
            upsertLocal(history)
            Result.Success(history)
        } catch (e: Exception) {
            // Leave the optimistic entry in place; next syncHistory will
            // reconcile if/when the backend has the canonical version.
            Result.Error("Failed to update progress: ${e.message}", e)
        }
    }

    private fun upsertLocal(history: WatchHistory) {
        val current = historyFlow.value.toMutableList()
        current.removeAll { it.contentId == history.contentId }
        current.add(history)
        historyFlow.value = current
    }

    private fun nowMs(): Long = kotlinx.datetime.Clock.System.now().toEpochMilliseconds()

    override suspend fun getResumePosition(contentId: String): Long? {
        val entry = historyFlow.value.find { it.contentId == contentId } ?: return null
        // Live channels always join at the live edge — a stored position is
        // meaningless (and harmful: it forces a seek into a live window).
        if (entry.contentType == ContentType.CHANNEL) return null
        val duration = entry.durationMs ?: return entry.resumePositionMs
        // Practically finished (>95%) → restart from the beginning next time.
        if (duration > 0 && entry.resumePositionMs >= duration * 95 / 100) return 0L
        return entry.resumePositionMs.coerceIn(0L, duration)
    }

    override suspend fun removeHistory(contentId: String): Result<Unit> = try {
        watchHistoryApi.removeHistory(contentId)
        historyFlow.value = historyFlow.value.filter { it.contentId != contentId }
        Result.Success(Unit)
    } catch (e: Exception) {
        Result.Error("Failed to remove history: ${e.message}", e)
    }

    override suspend fun syncHistory(): Result<Unit> = try {
        val fromBackend = watchHistoryApi.getHistory().map { dto ->
            WatchHistory(
                id = dto.id,
                userId = dto.userId,
                contentId = dto.contentId,
                contentType = parseContentType(dto.contentType),
                contentName = dto.contentName,
                contentLogoUrl = dto.contentLogoUrl,
                streamUrl = dto.streamUrl,
                resumePositionMs = dto.resumePositionMs,
                durationMs = dto.durationMs,
                lastWatchedAt = dto.lastWatchedAt,
                seriesSourceId = dto.seriesSourceId,
                seriesId = dto.seriesId?.toInt()
            )
        }
        // Merge: keep any LOCAL entries (e.g. an in-flight save the backend
        // hasn't acknowledged yet) instead of letting the backend's snapshot
        // silently wipe them out.
        val backendIds = fromBackend.map { it.contentId }.toSet()
        val localPending = historyFlow.value.filter { it.contentId !in backendIds }
        historyFlow.value = (fromBackend + localPending).sortedByDescending { it.lastWatchedAt }
        Result.Success(Unit)
    } catch (e: Exception) {
        Result.Error("Failed to sync history: ${e.message}", e)
    }

    private fun parseContentType(type: String): ContentType = try {
        ContentType.valueOf(type)
    } catch (_: Exception) {
        ContentType.CHANNEL
    }
}
